// Shared presentational primitives. No data fetching here.
import { Link } from 'react-router-dom';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { AlertCircle, Info, LoaderCircle, X } from 'lucide-react';
import { ApiError } from '../lib/api';

export function PageHeading({ eyebrow, title, description, action }: { eyebrow: string; title: string; description?: string; action?: ReactNode }) {
  return (
    <header className="page-heading">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        {description && <p className="muted">{description}</p>}
      </div>
      {action && <div className="row">{action}</div>}
    </header>
  );
}

export function Panel({ title, description, action, children, note, flush = false }: {
  title?: ReactNode; description?: ReactNode; action?: ReactNode; children: ReactNode; note?: ReactNode; flush?: boolean;
}) {
  return (
    <section className="panel">
      {(title || action) && (
        <header className="panel-header">
          <div className="grow">
            {title && <h2>{title}</h2>}
            {description && <p className="muted small">{description}</p>}
          </div>
          {action}
        </header>
      )}
      <div className={`panel-body${flush ? ' flush' : ''}`}>{children}</div>
      {note && <div className="panel-note">{note}</div>}
    </section>
  );
}

export function Notice({ tone = 'info', children, icon = true }: { tone?: 'info' | 'error' | 'success' | 'warn'; children: ReactNode; icon?: boolean }) {
  const cls = tone === 'warn' ? '' : tone;
  return (
    <div className={`notice ${cls}`} role={tone === 'error' ? 'alert' : 'status'}>
      {icon && (tone === 'error' ? <AlertCircle size={16} aria-hidden /> : <Info size={16} aria-hidden />)}
      <div className="grow">{children}</div>
    </div>
  );
}

export function ErrorNotice({ error, retry }: { error: unknown; retry?: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  // Long forms put the error far above the button that caused it; bring it into view.
  useEffect(() => { if (error) box.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' }); }, [error]);
  if (!error) return null;
  const e = error instanceof ApiError ? error : null;
  const message = error instanceof Error ? error.message : 'Something went wrong.';
  return (
    <div ref={box}><Notice tone="error">
      <p>{message}</p>
      {e?.code === 'unauthenticated' && /api key/i.test(e.message) && <p className="small">Add your API key under Settings.</p>}
      {e?.code === 'plan_limit' && <p className="small"><Link to="/billing">See plans</Link></p>}
      {e?.code === 'identifier_required' && <p className="small"><Link to="/trade/profile#registrations">Add registrations</Link></p>}
      {e?.details && Array.isArray((e.details as { did_you_mean?: string[] }).did_you_mean) && (
        <p className="small">Did you mean: {((e.details as { did_you_mean: string[] }).did_you_mean).join(', ')}?</p>
      )}
      <div className="row small">
        {retry && <button type="button" className="button ghost sm" onClick={retry}>Try again</button>}
        {e?.requestId && <span className="muted tiny">Request id {e.requestId}</span>}
      </div>
    </Notice></div>
  );
}

export function Loading({ label = 'Calculating…' }: { label?: string }) {
  return <div className="loading" role="status"><LoaderCircle className="spin" size={18} aria-hidden />{label}</div>;
}

export function Empty({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return <div className="empty"><h3>{title}</h3>{children && <p>{children}</p>}{action}</div>;
}

export function Badge({ tone = 'neutral', children, title }: { tone?: 'neutral' | 'good' | 'amber' | 'bad' | 'info'; children: ReactNode; title?: string }) {
  return <span className={`badge ${tone}`} title={title}>{children}</span>;
}

export function Stat({ label, value, sub, icon }: { label: string; value: ReactNode; sub?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="stat">
      <div className="stat-label"><span>{label}</span>{icon}</div>
      <div className="stat-value">{value}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return <label className="field"><span>{label}</span>{children}{hint && <span className="field-hint">{hint}</span>}</label>;
}

export function Segmented<T extends string>({ value, options, onChange, label }: {
  value: T; options: { value: T; label: string }[]; onChange: (v: T) => void; label: string;
}) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: { value: T; label: string }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((t) => (
        <button key={t.value} role="tab" type="button" aria-selected={t.value === value} onClick={() => onChange(t.value)}>{t.label}</button>
      ))}
    </div>
  );
}

export function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const dialog = ref.current;
    const previous = document.activeElement;
    if (dialog && !dialog.open) dialog.showModal?.();
    return () => {
      dialog?.close?.();
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, []);
  return (
    <dialog ref={ref} className="modal" aria-labelledby={titleId} onCancel={(e) => { e.preventDefault(); onClose(); }}>
      <header className="modal-header">
        <h2 id={titleId}>{title}</h2>
        <button className="icon-button" type="button" aria-label="Close dialog" onClick={onClose}><X size={18} /></button>
      </header>
      {children}
    </dialog>
  );
}

export function Disclosure({ label, children, defaultOpen = false }: { label: string; children: ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <details className="disclosure" open={open} onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}>
      <summary>{label}</summary>
      <div>{children}</div>
    </details>
  );
}

export function Slider({ label, value, min, max, step, onChange, format, hint, disabled }: {
  label: string; value: number; min: number; max: number; step: number; onChange: (v: number) => void;
  format: (v: number) => string; hint?: string; disabled?: boolean;
}) {
  const id = useId();
  return (
    <div className="slider">
      <div className="slider-top"><label htmlFor={id}>{label}</label><output htmlFor={id}>{format(value)}</output></div>
      <input id={id} type="range" min={min} max={max} step={step} value={value} disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))} aria-valuetext={format(value)} />
      <div className="slider-foot"><span>{format(min)}</span>{hint && <span>{hint}</span>}<span>{format(max)}</span></div>
    </div>
  );
}
